<template>
  <div class="container-fluid px-4 mt-4">
    <div class="d-flex align-items-center justify-content-between mb-3">
      <h5 class="fw-bold mb-0">정기권 변경 이력</h5>
      <span class="text-muted small" v-if="histories.length > 0">
        총 {{ totalCount }}건
      </span>
    </div>

    <div class="card shadow-sm">
      <div class="card-body p-4">
        <div v-if="loading" class="text-center py-5 text-muted">불러오는 중...</div>
        <div v-if="loadError" class="alert alert-danger">{{ loadError }}</div>

        <div v-else>
          <div v-if="histories.length === 0" class="text-center text-muted py-4">
            변경 이력이 없습니다.
          </div>

          <div v-else class="table-responsive">
            <table class="table table-hover align-middle table-sm">
              <thead class="table-dark">
                <tr>
                  <th style="width: 80px">ChangeSeq</th>
                  <th style="width: 80px">PassSeq</th>
                  <th style="width: 80px">UserSeq</th>
                  <th style="width: 100px">차량번호</th>
                  <th style="width: 80px">AreaSeq</th>
                  <th style="width: 120px">주차장명</th>
                  <th style="width: 80px">FeeSeq</th>
                  <th>DiscountSeq</th>
                  <th style="width: 140px">시작일</th>
                  <th style="width: 140px">종료일</th>
                  <th style="width: 100px">상태</th>
                  <th style="width: 120px">등록일</th>
                  <th style="width: 80px">결제Seq</th>
                  <th style="width: 80px">WaitingSeq</th>
                  <th style="width: 100px">최근결제월</th>
                  <th style="width: 80px">변경타입</th>
                  <th style="width: 140px">변경일시</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="h in histories" :key="h.ChangeSeq" class="border-bottom">
                  <td><code class="text-primary fw-bold">{{ h.ChangeSeq }}</code></td>
                  <td><code>{{ h.PassSeq }}</code></td>
                  <td><code>{{ h.UserSeq }}</code></td>
                  <td><code class="text-info fw-bold" v-if="h.CarNumber">{{ h.CarNumber }}</code><span v-else class="text-muted">-</span></td>
                  <td><code>{{ h.AreaSeq }}</code></td>
                  <td><span class="fw-bold" v-if="h.AreaName">{{ h.AreaName }}</span><span v-else class="text-muted">-</span></td>
                  <td><code>{{ h.FeeSeq }}</code></td>
                  <td><code v-if="h.DiscountSeq" class="text-warning">{{ h.DiscountSeq }}</code><span v-else class="text-muted">-</span></td>
                  <td class="small">{{ formatDateTime(h.StartDate) }}</td>
                  <td class="small">{{ formatDateTime(h.EndDate) }}</td>
                  <td>
                    <span v-if="h.Status === 'ACTIVE'" class="badge bg-success">{{ h.Status }}</span>
                    <span v-else-if="h.Status === 'EXPIRED'" class="badge bg-secondary">{{ h.Status }}</span>
                    <span v-else class="badge bg-warning">{{ h.Status }}</span>
                  </td>
                  <td class="small">{{ formatDateTime(h.CreatedDate) }}</td>
                  <td><code v-if="h.LastPaymentSeq">{{ h.LastPaymentSeq }}</code><span v-else class="text-muted">-</span></td>
                  <td><code v-if="h.WaitingSeq">{{ h.WaitingSeq }}</code><span v-else class="text-muted">-</span></td>
                  <td><code v-if="h.LastPaidMonth">{{ h.LastPaidMonth }}</code><span v-else class="text-muted">-</span></td>
                  <td>
                    <span v-if="h.ChangeType === 'INSERT'" class="badge bg-success">{{ h.ChangeType }}</span>
                    <span v-else-if="h.ChangeType === 'UPDATE'" class="badge bg-info">{{ h.ChangeType }}</span>
                    <span v-else-if="h.ChangeType === 'DELETE'" class="badge bg-danger">{{ h.ChangeType }}</span>
                    <span v-else class="badge bg-secondary">{{ h.ChangeType }}</span>
                  </td>
                  <td class="small">{{ formatDateTime(h.ChangedDate) }}</td>
                </tr>
              </tbody>
            </table>
          </div>

          <!-- 페이징 -->
          <div v-if="totalPages > 1" class="d-flex justify-content-center mt-4 gap-2">
            <button 
              type="button" 
              class="btn btn-sm btn-outline-secondary" 
              :disabled="currentPage === 1"
              @click="currentPage = 1; fetchHistories()"
            >
              ⬅️ 처음
            </button>
            <button 
              type="button" 
              class="btn btn-sm btn-outline-secondary" 
              :disabled="currentPage === 1"
              @click="currentPage--; fetchHistories()"
            >
              이전
            </button>
            <span class="btn btn-sm btn-light" disabled>
              {{ currentPage }} / {{ totalPages }} ({{ totalCount }}건)
            </span>
            <button 
              type="button" 
              class="btn btn-sm btn-outline-secondary" 
              :disabled="currentPage === totalPages"
              @click="currentPage++; fetchHistories()"
            >
              다음
            </button>
            <button 
              type="button" 
              class="btn btn-sm btn-outline-secondary" 
              :disabled="currentPage === totalPages"
              @click="currentPage = totalPages; fetchHistories()"
            >
              마지막 ➡️
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios'

export default {
  name: 'AdminActiveParkingPassChangeHistory',
  data() {
    return {
      histories: [],
      totalCount: 0,
      currentPage: 1,
      pageSize: 50,
      totalPages: 1,
      loading: false,
      loadError: null
    }
  },
  mounted() {
    this.fetchHistories()
  },
  methods: {
    async fetchHistories() {
      this.loading = true
      this.loadError = null
      try {
        const res = await axios.get(`${process.env.VUE_APP_API_BASE}/api/admin/active-parking-pass-change-history`, {
          params: {
            page: this.currentPage,
            pageSize: this.pageSize
          }
        })
        this.histories = res.data.data || []
        this.totalCount = res.data.totalCount || 0
        this.totalPages = res.data.totalPages || 1
        if (this.currentPage > this.totalPages && this.totalPages > 0) {
          this.currentPage = this.totalPages
        }
      } catch (e) {
        this.loadError = e.response?.data?.message || '변경 이력 조회 실패'
        console.error('fetchHistories error:', e)
      } finally {
        this.loading = false
      }
    },
    formatDateTime(dateStr) {
      if (!dateStr) return '-'
      const d = new Date(dateStr)
      return d.toLocaleString('ko-KR', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit'
      })
    }
  }
}
</script>

<style scoped>
.table-hover tbody tr:hover {
  background-color: rgba(0, 123, 255, 0.05);
}

code {
  padding: 2px 6px;
  background-color: #f5f5f5;
  border-radius: 3px;
  font-family: 'Courier New', monospace;
  font-size: 0.85rem;
}

.badge {
  padding: 4px 8px;
  font-size: 0.75rem;
}
</style>
